import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  getDocs,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  limit,
  orderBy,
  startAfter,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { useToast } from '../components/Toast';
import { logger } from '../utils/logger';

const PortfolioContext = createContext(null);

const PORTFOLIO_PAGE_SIZE = 50;

export function PortfolioProvider({ children }) {
  const { firebaseUser, teamOwnerId } = useAuth();
  const { showToast } = useToast();

  const [portfolio, setPortfolio] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [portfolioLastDoc, setPortfolioLastDoc] = useState(null);
  const [hasMorePortfolio, setHasMorePortfolio] = useState(true);

  useEffect(() => {
    if (!firebaseUser) return;
    const ownerIdToUse = teamOwnerId || firebaseUser.uid;

    const q = query(
      collection(db, 'portfolio'),
      where('userId', '==', ownerIdToUse),
      orderBy('createdAt', 'desc'),
      limit(PORTFOLIO_PAGE_SIZE)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const portfolioData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setPortfolio(portfolioData);
        setPortfolioLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
        setHasMorePortfolio(snapshot.docs.length >= PORTFOLIO_PAGE_SIZE);
        setPortfolioLoading(false);
      },
      (error) => {
        logger.error('Error loading portfolio:', error);
        setPortfolioLoading(false);
        showToast('Failed to load portfolio. Please refresh the page.', 'error');
      }
    );

    return unsubscribe;
  }, [firebaseUser, teamOwnerId, showToast]);

  const loadMorePortfolio = async () => {
    if (!portfolioLastDoc || !hasMorePortfolio) return;
    try {
      const ownerIdToUse = teamOwnerId || firebaseUser.uid;
      const q = query(
        collection(db, 'portfolio'),
        where('userId', '==', ownerIdToUse),
        orderBy('createdAt', 'desc'),
        startAfter(portfolioLastDoc),
        limit(PORTFOLIO_PAGE_SIZE)
      );
      const snapshot = await getDocs(q);
      const moreData = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPortfolio((prev) => [...prev, ...moreData]);
      setPortfolioLastDoc(snapshot.docs[snapshot.docs.length - 1] || null);
      setHasMorePortfolio(snapshot.docs.length >= PORTFOLIO_PAGE_SIZE);
    } catch (error) {
      logger.error('Error loading more portfolio items:', error);
      showToast('Failed to load more items. Please try again.', 'error');
    }
  };

  const addPortfolioToFirebase = async (portfolioData) => {
    const ownerIdToUse = teamOwnerId || firebaseUser.uid;
    try {
      const docRef = await addDoc(collection(db, 'portfolio'), {
        ...portfolioData,
        userId: ownerIdToUse,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return { success: true, id: docRef.id };
    } catch (error) {
      logger.error('Error adding portfolio item:', error);
      return { success: false, error: error.message };
    }
  };

  const deletePortfolioFromFirebase = async (portfolioId) => {
    try {
      await deleteDoc(doc(db, 'portfolio', portfolioId));
      return { success: true };
    } catch (error) {
      logger.error('Error deleting portfolio item:', error);
      return { success: false, error: error.message };
    }
  };

  const value = useMemo(
    () => ({
      portfolio,
      portfolioLoading,
      addPortfolioToFirebase,
      deletePortfolioFromFirebase,
      loadMorePortfolio,
      hasMorePortfolio,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [portfolio, portfolioLoading, hasMorePortfolio]
  );

  return <PortfolioContext.Provider value={value}>{children}</PortfolioContext.Provider>;
}

export function usePortfolio() {
  const context = useContext(PortfolioContext);
  if (!context) throw new Error('usePortfolio must be used within PortfolioProvider');
  return context;
}
